import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AuthContext } from '../contexts/AuthContext';
import { getPlan } from '../utils/apiServices';
import '../stylesheets/actionPlanView.css';

const ActionPlanView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user || !slug) return;
    
    getPlan(slug)
      .then((data) => {
        setPlan(data);
        setLoading(false);
      })
      .catch((err) => {
        setError('No se pudo cargar el plan de negocios.');
        setLoading(false);
      });
  }, [user, slug]);

  const handleBack = () => {
    navigate('/plans');
  };

  const handlePrint = () => {
    window.print();
  };

  if (!user) {
    return <Navigate to="/login/" replace />;
  }

  if (loading) {
    return (
      <div className="action-plan-view">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando plan de acción...</p>
        </div>
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="action-plan-view">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>Error al cargar</h2>
          <p>{error || 'Plan no encontrado'}</p>
          <button className="btn-back" onClick={handleBack}>
            Volver a Mis Planes
          </button>
        </div>
      </div>
    );
  }

  if (!plan.action_plan_md) {
    return (
      <div className="action-plan-view">
        <div className="error-container">
          <div className="error-icon">📋</div>
          <h2>Sin plan de acción</h2>
          <p>Este plan de negocios aún no tiene un plan de acción generado.</p>
          <button className="btn-back" onClick={handleBack}>
            Volver a Mis Planes
          </button>
        </div>
      </div>
    );
  }

  const generatedDate = plan.action_plan_generated_at 
    ? new Date(plan.action_plan_generated_at).toLocaleString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '';

  return (
    <div className="action-plan-view">
      <div className="action-plan-header">
        <button className="btn-back" onClick={handleBack}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Volver
        </button>
        <button className="btn-print no-print" onClick={handlePrint}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
            <rect x="6" y="14" width="12" height="8"/>
          </svg>
          Imprimir
        </button>
      </div>

      <div className="action-plan-content">
        <div className="action-plan-title-section">
          <h1>{plan.title}</h1>
          <div className="action-plan-meta">
            <span className="meta-item">
              <span className="meta-icon">👤</span>
              {plan.owner}
            </span>
            <span className="meta-item">
              <span className="meta-icon">📅</span>
              Generado: {generatedDate}
            </span>
          </div>
        </div>

        <div className="action-plan-body">
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({node, ...props}) => <h2 className="markdown-h1" {...props} />,
              h2: ({node, ...props}) => <h3 className="markdown-h2" {...props} />,
              h3: ({node, ...props}) => <h4 className="markdown-h3" {...props} />,
              ul: ({node, ...props}) => <ul className="markdown-ul" {...props} />,
              ol: ({node, ...props}) => <ol className="markdown-ol" {...props} />,
              li: ({node, ...props}) => <li className="markdown-li" {...props} />,
              p: ({node, ...props}) => <p className="markdown-p" {...props} />,
              strong: ({node, ...props}) => <strong className="markdown-strong" {...props} />,
              em: ({node, ...props}) => <em className="markdown-em" {...props} />,
              blockquote: ({node, ...props}) => <blockquote className="markdown-blockquote" {...props} />,
              code: ({node, inline, ...props}) => 
                inline 
                  ? <code className="markdown-code-inline" {...props} />
                  : <code className="markdown-code-block" {...props} />,
            }}
          >
            {plan.action_plan_md}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

export default ActionPlanView;
